import { useState, useEffect, useMemo } from 'react';
import { jobTitlesApi } from '../services/api/jobTitles';
import type { JobTitle } from '../types';

export interface JobTitleGroup {
  category: string;
  titles: { id: string; name: string; description: string | null }[];
  disabled: boolean;
}

const CATEGORY_ORDER = ['Grow', 'Build', 'Deliver', 'Sustain / Biz Ops'];

export function useJobTitles() {
  const [jobTitles, setJobTitles] = useState<JobTitle[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetch = async () => {
      try {
        setLoading(true);
        const data = await jobTitlesApi.getAll();
        if (!cancelled) {
          setJobTitles(data);
        }
      } catch (err) {
        console.error('Failed to fetch job titles:', err);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetch();
    return () => { cancelled = true; };
  }, []);

  const jobTitleGroups = useMemo<JobTitleGroup[]>(() => {
    // Pre-seed the four canonical functions so each header is always rendered,
    // even when no roles are assigned to it (Sustain / Biz Ops today).
    const grouped = new Map<string, JobTitleGroup>();
    for (const cat of CATEGORY_ORDER) {
      grouped.set(cat, { category: cat, titles: [], disabled: true });
    }

    for (const title of jobTitles) {
      const cat = title.category || 'OTHER';
      if (!grouped.has(cat)) {
        grouped.set(cat, { category: cat, titles: [], disabled: true });
      }
      grouped.get(cat)!.titles.push({
        id: title.id,
        name: title.name,
        description: title.description,
      });
    }

    // Sort groups by CATEGORY_ORDER, then alphabetically for unknown categories
    const sorted = [...grouped.values()].sort((a, b) => {
      const aIdx = CATEGORY_ORDER.indexOf(a.category);
      const bIdx = CATEGORY_ORDER.indexOf(b.category);
      if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
      if (aIdx !== -1) return -1;
      if (bIdx !== -1) return 1;
      return a.category.localeCompare(b.category);
    });

    // A group is interactive only when it has at least one role.
    return sorted.map(g => ({ ...g, disabled: g.titles.length === 0 }));
  }, [jobTitles]);

  return { jobTitleGroups, jobTitles, loading };
}
